/**
 * src/ui/espera.ts — as telas que NÃO são mídia.
 *
 * PLAYER §3 (tabela de estados) e §4.4. A regra que atravessa este arquivo inteiro:
 * **NENHUMA mensagem de erro técnico na tela da TV** (§5.2). O cliente da loja não
 * pode ler "401 UNAUTHENTICATED"; ele pode ler "Sem internet" ou
 * "Esta tela foi removida da conta. Pareie novamente."
 *
 * Todas as funções devolvem NÓS de DOM e não tocam em estado global: a casca
 * (`main.ts`) decide qual mostrar. Isso mantém cada tela testável isoladamente.
 */

/**
 * Estado visível do receptor — espelha a tabela de PLAYER §3.
 *
 * O web NÃO tem mais os estados de pareamento por código: eles viraram o
 * VÍNCULO por QR (`vinculo.ts`), que é um fluxo diferente de ponta a ponta
 * (HYBRID §1). Um código de 6 dígitos digitável não existe mais no produto web.
 */
export type ScreenState =
  | 'boot'
  | 'vinculo-waiting' /** QR + código de conferência + URL de fallback */
  | 'vinculo-expired' /** "O pedido expirou" */
  | 'vinculo-denied' /** "Vínculo recusado no celular" */
  | 'vinculo-offline' /** QR + faixa "Sem internet" */
  | 'vinculo-revoked' /** "Esta tela foi removida da conta." */
  | 'empty' /** vinculada, fila vazia */
  | 'stale' /** sem contato há > 7 dias (D4) */
  | 'offline-idle' /** sem rede e sem mídia em cache: cinza, sem erro técnico */

export interface EsperaOptions {
  /** Nome da tela, quando conhecido ("TV Balcão"). */
  screenName?: string;
  /** `true` = mostra a faixa "Sem internet". */
  offline?: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

/** "Conectado! Buscando anúncios..." — §3, estado "Pareado". */
export function renderPareado(screenName?: string): HTMLElement {
  const root = el('section', 'tela tela--pareado');
  root.dataset['estado'] = 'pareado';
  root.setAttribute('role', 'status');
  if (screenName) {
    root.append(el('h1', 'tela__titulo', screenName));
  }
  root.append(el('p', 'tela__mensagem', 'Conectado! Buscando anúncios...'));
  return root;
}

/**
 * "Esta tela foi removida da conta." — o que o receptor mostra depois de um
 * `401 DEVICE_TOKEN_REVOKED` (§2 do PLAYER, critério 10). Sem "pareie novamente
 * com o código": o web volta ao fluxo de vínculo.
 */
export function renderVinculoRevogado(): HTMLElement {
  const root = el('section', 'tela tela--revogado');
  root.dataset['estado'] = 'vinculo-revogado';
  root.setAttribute('role', 'status');
  root.append(el('h1', 'tela__titulo', 'Esta tela foi removida da conta.'));
  root.append(el('p', 'tela__mensagem', 'Ela precisa ser vinculada de novo para voltar a exibir anúncios.'));
  return root;
}

/**
 * "Nenhum anúncio publicado para esta tela" — vinculada, fila vazia (§3/§6).
 * Mostra o nome da tela; sem código no rodapé (não há mais código).
 */
export function renderSemAnuncios(options: EsperaOptions = {}): HTMLElement {
  const root = el('section', 'tela tela--vazia');
  root.dataset['estado'] = 'sem-anuncios';
  if (options.screenName) {
    root.append(el('h1', 'tela__titulo', options.screenName));
  }
  root.append(el('p', 'tela__mensagem', 'Nenhum anúncio publicado para esta tela'));
  return root;
}

/**
 * "Sem conexão" (§4.4): CINZA, sem texto de erro técnico, e SEM APAGAR a
 * programação. O lojista não pode ver "ERR_INTERNET_DISCONNECTED" nem a TV pode
 * perder a fila por causa disso.
 */
export function renderSemConexao(options: EsperaOptions = {}): HTMLElement {
  const root = el('section', 'tela tela--sem-conexao');
  root.dataset['estado'] = 'sem-conexao';
  if (options.screenName) {
    root.append(el('h1', 'tela__titulo', options.screenName));
  }
  root.append(el('p', 'tela__mensagem', 'Sem conexão'));
  return root;
}


/**
 * D4: após 7 dias sem contato com o backend, faixa discreta no rodapé.
 * A programação CONTINUA rodando — a faixa é aviso, não bloqueio.
 */
export function renderProgramacaoDesatualizada(): HTMLElement {
  const root = el('section', 'tela tela--desatualizada');
  root.dataset['estado'] = 'desatualizada';
  root.append(el('p', 'tela__rodape', 'Programação pode estar desatualizada'));
  return root;
}

/**
 * Faixa genérica de estado. `tom` é o contraste: 'aviso' (âmbar) ou 'neutro'
 * (cinza). NUNCA vermelho de erro — o cliente não deve ver alerta de sistema.
 */
export function renderFaixa(texto: string, tom: 'aviso' | 'neutro' = 'neutro'): HTMLElement {
  const faixa = el('p', `faixa faixa--${tom}`, texto);
  faixa.dataset['papel'] = 'faixa';
  return faixa;
}

/**
 * Aviso de item que não pode ser exibido (PLAYER §6 — pular e REPORTAR).
 *
 * Por que uma seção de ESTADO e não só uma faixa: o item é a MÍDIA, e o
 * lojista precisa entender que algo específico falhou (codec, download
 * truncado, timeout de decodificação). A faixa genérica não carrega esse
 * "isto é sobre UM anúncio", e o `renderSemAnuncios` é sobre a FILA VAZIA,
 * não sobre uma falha pontual.
 *
 * O `motivo` é texto PT-BR curto (3-5 palavras) — frases longas viram ruído
 * numa TV Box vista de longe. O id do anúncio vai no `dataset` para que
 * ferramentas de diagnóstico (painel de demo) consigam correlacionar com o log.
 */
export function renderItemIndisponivel(
  options: { motivo: string; adId: string },
): HTMLElement {
  const root = el('section', 'tela tela--item-indisponivel');
  root.dataset['estado'] = 'item-indisponivel';
  root.dataset['adId'] = options.adId;
  root.append(el('p', 'tela__mensagem', 'Este anúncio não pôde ser exibido.'));
  root.append(el('p', 'tela__rodape', options.motivo));
  return root;
}

/** Texto curto para o motivo da falha. Decodificado a partir do `MediaUnavailableError`. */
export function motivoCurto(reason: unknown): string {
  if (reason === undefined || reason === null) return 'mídia indisponível';
  if (reason instanceof Error) {
    // Erros de decodificação no navegador chegam como `MEDIA_ERR_*` no `video.error.code`
    // mas a superfície já os transforma em Error com mensagem; usamos a mensagem
    // curta em vez do stack.
    const msg = reason.message || reason.name;
    if (msg.toLowerCase().includes('timeout')) return 'demorou para carregar';
    if (msg.toLowerCase().includes('codec') || msg.toLowerCase().includes('decode')) {
      return 'formato não suportado';
    }
    if (msg.toLowerCase().includes('network') || msg.toLowerCase().includes('fetch')) {
      return 'falha de rede';
    }
    return 'mídia indisponível';
  }
  if (typeof reason === 'string') {
    if (reason === 'blob vazio') return 'arquivo vazio';
    if (reason.toLowerCase().includes('timeout')) return 'demorou para carregar';
    return reason;
  }
  return 'mídia indisponível';
}
